import TextEditorCustomize from '@/components/TextEditorCustomize'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { createCustomerNote } from '@/modules/Customer/services'
import { CreateCustomerNotePayload } from '@/modules/Customer/types'
import { ModalProps } from '@/types/shared'
import { Save, X } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Dialog } from 'vnetwork-ui'

export interface AddNoteModalProps extends ModalProps {
    customer_id: number
}

interface EditorValue {
    text: string
    attachments: any[]
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
    customer_id,
    onClose,
    open,
}) => {
    const { t } = useTranslation()

    const [editorValue, setEditorValue] = useState<EditorValue>({
        text: '',
        attachments: [],
    })
    const [isPinned, setIsPinned] = useState<boolean>(true)

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: CreateCustomerNotePayload) =>
                createCustomerNote(customer_id, data),
        },
        {
            successMessage: t('Customer note created successfully'),
            reactQueryKeys: [QUERY_KEYS.CUSTOMER_LOGS],
            onSuccess: () => {
                onClose()
            },
        }
    )

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await mutateAsync({
            note_description: editorValue.text,
            is_pinned: isPinned,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('add-note')}
            maxWidth="md"
        >
            <form onSubmit={onSubmit} className="space-y-4">
                {/* Note Editor */}
                <TextEditorCustomize
                    label={t('note-content')}
                    placeholder={t('enter-note-content')}
                    value={editorValue}
                    onChange={setEditorValue}
                />

                {/* Pin Option */}
                <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/20">
                    <Checkbox
                        checked={isPinned}
                        onChange={(e) => setIsPinned(e.target.checked)}
                    />
                    <label className="text-sm font-medium cursor-pointer">
                        {t('pin-note')} ({t('Pinned notes appear at the top')})
                    </label>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                        startIcon={<X className="h-4 w-4" />}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isPending || !editorValue.text.trim()}
                        loading={isPending}
                        startIcon={<Save className="h-4 w-4" />}
                    >
                        {t('save-note')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default AddNoteModal
